import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { IChatConversation, INewConversationData } from '@fi-shared/fi-chat/fi-chat';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { ObjectTypes } from '@fi-shared/types';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { FiDndOverlayComponent } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-overlay.component';

@Component({
    selector: 'fi-chat-conversation',
    template: `
        <fi-dnd-overlay #fiDndOverlayComponent [parentElementRef]="elementRef" (uploadFiles)="uploadFiles($event)" />
        @if (isNewConversation) {
            <fi-chat-conversation-new
                [conversation]="conversation"
                [isDragging$]="fiDndOverlayComponent.fiDndHelper.dragging$"
                (startNewConversation)="onStartNewConversation($event)"
                [newMediaBankReferences$]="newMediaBankFileReferences$.asObservable()"
            />
        } @else {
            <fi-chat-conversation-existing
                [conversation]="conversation"
                [isMessageInputFocus]="!isMobilePhone()"
                [newMediaBankReferences$]="newMediaBankFileReferences$.asObservable()"
            />
        }
    `,
    styleUrls: ['./fi-chat-conversation.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation' },
    standalone: false,
})
export class FiChatConversationComponent implements OnInit, OnDestroy {
    @Input() conversation: IChatConversation;
    @Input() isNewConversation: boolean;
    @Output() startNewConversation = new EventEmitter<INewConversationData>();
    @Output() closeConversation = new EventEmitter<IChatConversation>();

    @ViewChild('fiDndOverlayComponent') fiDndOverlayComponent: FiDndOverlayComponent;
    protected newMediaBankFileReferences$ = new Subject<IMediaBankFileReference[]>();

    constructor(
        private fiChatService: FiChatService,
        private fiChatSyncService: FiChatSyncService,
        private detectDeviceService: DetectDevicesService,
        public elementRef: ElementRef,
        private fileUpload2Service: FiFileUpload2Service,
    ) {}

    ngOnInit(): void {
        this.subscribeToConversationRemoved();
        this.subscribeToConversationOpenMobile();
    }

    private subscribeToConversationRemoved(): void {
        this.fiChatSyncService.conversationRemoved$
            .pipe(
                filter((conversationId) => conversationId === this.conversation.Id),
                takeUntil(this.ngOnDestroy$),
            )
            .subscribe(() => {
                this.closeConversation.emit(this.conversation);
            });
    }

    private subscribeToConversationOpenMobile(): void {
        if (this.isMobilePhone()) {
            this.fiChatService.onOpenConversationById$
                .pipe(
                    takeUntil(this.ngOnDestroy$),
                    filter((conversationId) => conversationId !== this.conversation.Id),
                )
                .subscribe((conversationId) => {
                    // https://chainformation.atlassian.net/browse/FI-4115
                    // if user opens new conversation in mobile (for example by pressing on push notifications)
                    this.closeConversation.emit(this.conversation);
                });
        }
    }

    protected uploadFiles(files: File[]): void {
        this.fileUpload2Service.silentUploadFiles(files).then((mediaBankReferences: IMediaBankFileReference[]) => {
            this.newMediaBankFileReferences$.next(mediaBankReferences);
        });
    }

    protected isMobilePhone(): boolean {
        return this.detectDeviceService.isMobilePhone();
    }

    protected onStartNewConversation($event: INewConversationData): void {
        this.startNewConversation.emit($event);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
